import React, { useEffect, useRef, useState } from 'react';
import { sendGiftLinkBatch } from 'Api/apiFunctions';
import styles from './ContactShareModal.module.css';

// ContactShareModal
//
// Capability-driven share UX for the gift status page.
//
//   • Chrome Android (Web Contacts API)
//     ─ open(): immediately calls navigator.contacts.select() to invoke
//       the native picker; the user's selected contacts come back here
//       and this modal displays them with checkboxes for confirm/dedupe.
//
//   • iOS Safari / iOS Chrome / Desktop with Web Share API
//     ─ open(): bypasses the modal and calls navigator.share() directly,
//       handing off to the native share sheet (iOS gives the user the
//       iMessage/Mail/etc. picker — same outcome as our in-app modal).
//
//   • Everything else (rare desktop)
//     ─ open(): renders a fallback in-app form where the user types a
//       phone OR email manually, then we POST the same batch endpoint.
//
// On confirm, the modal POSTs to `sendGiftLinkBatch` (see apiFunctions.js
// stub) with { url, message, recipients }. BE owns the SMS/email send.

const detectMode = () => {
  if (typeof navigator === 'undefined') return 'manual';
  // Web Contacts API only ships on Chrome Android (and a few embedded
  // Chromium browsers). Both `navigator.contacts` and `ContactsManager`
  // need to exist — feature-detect, don't UA-sniff.
  if (
    'contacts' in navigator
    && typeof navigator.contacts.select === 'function'
    && 'ContactsManager' in window
  ) {
    return 'contacts-api';
  }
  if (typeof navigator.share === 'function') {
    return 'web-share';
  }
  return 'manual';
};

// Web Contacts API returns objects shaped like:
//   { name: ['John Doe'], tel: ['+15551234567'], email: ['j@x.com'] }
// Flatten + dedupe into our { name, phone, email } recipient list. A
// contact with both tel and email becomes one row (we'll send to both).
const normalizeContacts = (raw) => {
  if (!Array.isArray(raw)) return [];
  const out = [];
  raw.forEach((c, i) => {
    const name = (c.name && c.name[0]) || (c.tel && c.tel[0]) || (c.email && c.email[0]) || `Contact ${i + 1}`;
    const phone = (c.tel && c.tel[0]) || null;
    const email = (c.email && c.email[0]) || null;
    if (!phone && !email) return;
    out.push({
      id: `c-${i}-${phone || email}`,
      name: String(name).trim(),
      phone: phone ? String(phone).trim() : null,
      email: email ? String(email).trim() : null,
    });
  });
  return out;
};

const ContactShareModal = ({
  open,
  url,
  message,
  onClose,
  onSuccess,
  onError,
}) => {
  const [mode, setMode] = useState('manual'); // contacts-api | web-share | manual
  const [contacts, setContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  // Manual fallback fields.
  const [manualInput, setManualInput] = useState('');
  const startedRef = useRef(false);

  // On open: detect platform, kick off whichever flow applies.
  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setContacts([]);
      setSelectedIds(new Set());
      setError('');
      setManualInput('');
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    const m = detectMode();
    setMode(m);

    if (m === 'contacts-api') {
      // Open the native picker. Must run inside the user-gesture frame
      // that triggered open(); our caller calls open() in onClick, so
      // we're still inside that frame here.
      (async () => {
        try {
          const picked = await navigator.contacts.select(
            ['name', 'tel', 'email'],
            { multiple: true },
          );
          const list = normalizeContacts(picked);
          setContacts(list);
          // Pre-select everyone the user already picked in the native UI.
          setSelectedIds(new Set(list.map((c) => c.id)));
          if (list.length === 0) {
            setError('No contacts were selected.');
          }
        } catch (e) {
          // Most failures are user cancellations of the native picker;
          // close cleanly instead of nagging with an error.
          if (onClose) onClose();
        }
      })();
    } else if (m === 'web-share') {
      // No in-app modal — hand off to the native share sheet.
      (async () => {
        try {
          await navigator.share({
            title: 'Add a memory to my Projector gift',
            text: message,
            url,
          });
          if (onSuccess) onSuccess({ via: 'web-share' });
        } catch (e) {
          // AbortError = user dismissed the share sheet — silent close.
          if (e && e.name !== 'AbortError' && onError) onError(e);
        } finally {
          if (onClose) onClose();
        }
      })();
    }
    // 'manual' → fall through; modal renders the input UI.
  }, [open, url, message, onClose, onSuccess, onError]);

  if (!open) return null;
  // 'web-share' path closes itself synchronously above — don't paint.
  if (mode === 'web-share') return null;

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const buildManualRecipient = () => {
    const v = manualInput.trim();
    if (!v) return null;
    // Quick heuristic: anything with an "@" is treated as email; else as phone.
    const isEmail = v.includes('@');
    return isEmail
      ? { id: 'manual', name: v, email: v, phone: null }
      : { id: 'manual', name: v, phone: v, email: null };
  };

  const handleSend = async () => {
    setError('');
    let recipients;
    if (mode === 'contacts-api') {
      recipients = contacts.filter((c) => selectedIds.has(c.id));
      if (recipients.length === 0) {
        setError('Pick at least one contact.');
        return;
      }
    } else {
      // manual
      const r = buildManualRecipient();
      if (!r) {
        setError('Enter a phone number or email.');
        return;
      }
      recipients = [r];
    }
    setSending(true);
    try {
      const res = await sendGiftLinkBatch({
        url,
        message,
        recipients: recipients.map((r) => ({
          name: r.name,
          phone: r.phone || undefined,
          email: r.email || undefined,
        })),
      });
      if (onSuccess) onSuccess(res);
      if (onClose) onClose();
    } catch (e) {
      setError((e && e.message) || 'Sending failed. Please try again.');
      if (onError) onError(e);
    } finally {
      setSending(false);
    }
  };

  const isContactsMode = mode === 'contacts-api';
  const isManualMode = mode === 'manual';
  const selectedCount = isContactsMode ? selectedIds.size : (manualInput.trim() ? 1 : 0);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share gift link"
      className={styles.backdrop}
      onClick={(e) => { if (e.target === e.currentTarget && !sending) onClose && onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape' && !sending) onClose && onClose(); }}
      tabIndex={-1}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            {isContactsMode ? 'Share with contacts' : 'Share gift link'}
          </h3>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            disabled={sending}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {isContactsMode && (
            <>
              <p className={styles.intro}>
                Pick the people you want to send your gift link to. We&apos;ll
                send them a short message with your link.
              </p>
              {contacts.length === 0 && !error && (
                <div className={styles.emptyState}>Loading your selection…</div>
              )}
              {contacts.map((c) => {
                const checked = selectedIds.has(c.id);
                return (
                  <label key={c.id} className={styles.row}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={checked}
                      onChange={() => toggleSelected(c.id)}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={styles.rowName}>{c.name}</div>
                      <div className={styles.rowDetail}>
                        {[c.phone, c.email].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  </label>
                );
              })}
            </>
          )}

          {isManualMode && (
            <>
              <p className={styles.intro}>
                Type a phone number or email and we&apos;ll send the gift
                link there.
              </p>
              <div className={styles.manualWrap}>
                <label htmlFor="contact-share-manual" className={styles.manualLabel}>
                  Phone number or email
                </label>
                <input
                  id="contact-share-manual"
                  type="text"
                  className={styles.manualInput}
                  placeholder="+1 555 123 4567 or jane@example.com"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  disabled={sending}
                  autoFocus
                />
                <p className={styles.manualHelper}>
                  Your browser doesn&apos;t support picking from contacts, so
                  enter one recipient at a time.
                </p>
              </div>
            </>
          )}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <span className={styles.footerCount}>
            {selectedCount > 0
              ? `${selectedCount} recipient${selectedCount === 1 ? '' : 's'}`
              : 'No recipients yet'}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.sendBtn}
              onClick={handleSend}
              disabled={sending || selectedCount === 0}
            >
              {sending ? 'Sending…' : 'Send'}
              <span aria-hidden="true">›</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactShareModal;
